import React, { useEffect, useState } from 'react';
import { List, X, ForkKnife, Camera, Heart, Buildings, MusicNote, Star, Cake, Confetti, MicrophoneStage, GiftIcon, ShoppingCart, Sparkle, FlowerLotus } from '@phosphor-icons/react';

/**
 * FloatingSegmentMenu — bottom-right FAB on Instant Pricing page.
 * Click → opens a sheet listing all visible segments. Click a segment →
 * smooth-scrolls to that section (via data-testid anchor).
 *
 * props:
 *   segments: Array<{ id: string, label: string, icon?: ReactNode }>
 *   testId?: string
 */
const FloatingSegmentMenu = ({ segments = [], testId = 'segment-menu' }) => {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [activeId, setActiveId] = useState(null);

  // Hide while user is at the very top (form area) — appears once they start scrolling
  useEffect(() => {
    const onScroll = () => {
      setHidden(window.scrollY < 250);
      // Track which segment is currently in view
      let current = null;
      for (const s of segments) {
        const el = document.querySelector(`[data-testid="${s.id}"]`);
        if (el) {
          const r = el.getBoundingClientRect();
          if (r.top <= 120 && r.bottom > 120) { current = s.id; break; }
        }
      }
      setActiveId(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [segments]);

  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Lock body scroll while sheet is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
    return undefined;
  }, [open]);

  const jumpTo = (id) => {
    const el = document.querySelector(`[data-testid="${id}"]`);
    if (!el) return;
    setOpen(false);
    // Account for sticky header (~80px)
    const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  if (segments.length === 0) return null;

  return (
    <>
      {/* Floating Action Button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Jump to segment"
        className={`fixed z-40 right-4 sm:right-6 bottom-4 sm:bottom-6 transition-all duration-300 ${
          hidden || open ? 'opacity-0 translate-y-4 pointer-events-none' : 'opacity-100 translate-y-0'
        }`}
        data-testid={`${testId}-fab`}
      >
        <div className="bg-text-primary text-sand-50 rounded-full pl-4 pr-5 py-3 shadow-2xl flex items-center gap-2 hover:bg-brand-primary transition-colors active:scale-95">
          <List size={20} weight="bold" />
          <span className="text-sm font-bold tracking-[0.2em] uppercase">Menu</span>
        </div>
      </button>

      {/* Bottom Sheet */}
      {open && (
        <div
          className="fixed inset-0 z-[55] bg-black/40 backdrop-blur-[2px] flex items-end sm:items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid={`${testId}-sheet`}
        >
          <div className="bg-white w-full sm:w-[420px] sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col animate-slide-up">
            <div className="px-5 py-4 border-b border-[#E8E1D5] flex items-center justify-between flex-shrink-0">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-text-secondary font-bold">Jump to</div>
                <div className="font-heading text-2xl tracking-tighter font-light">Segments</div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-sand-100 text-text-secondary"
                data-testid={`${testId}-close`}
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              {segments.map((s) => {
                const isActive = activeId === s.id;
                return (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => jumpTo(s.id)}
                    className={`w-full flex items-center gap-3 px-5 py-3.5 text-left transition-colors ${
                      isActive ? 'bg-brand-primary/10 text-brand-primary' : 'hover:bg-sand-100 text-text-primary'
                    }`}
                    data-testid={`${testId}-item-${s.id}`}
                  >
                    <span className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${isActive ? 'bg-brand-primary text-white' : 'bg-sand-100 text-text-secondary'}`}>
                      {s.icon || <Sparkle size={16} />}
                    </span>
                    <span className="flex-1 text-sm font-medium">{s.label}</span>
                    {isActive && (
                      <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-brand-primary">Now</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="px-5 py-3 border-t border-[#E8E1D5] text-center text-[10px] tracking-[0.2em] uppercase text-text-secondary font-bold flex-shrink-0">
              Tap any segment to jump
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.25s ease-out; }
      `}</style>
    </>
  );
};

// Pre-defined segment metadata reused by InstantPricingPage
export const SEGMENT_ICONS = {
  caterer: <ForkKnife size={16} />,
  decorator: <Heart size={16} />,
  custom_decor: <Sparkle size={16} />,
  flowers: <FlowerLotus size={16} />,
  photographer: <Camera size={16} />,
  venue: <Buildings size={16} />,
  professional: <Star size={16} />,
  entertainment_pro: <MicrophoneStage size={16} />,
  dj: <MusicNote size={16} />,
  equipment: <GiftIcon size={16} />,
  birthday_deco: <Cake size={16} />,
  cake: <Cake size={16} />,
  cart: <ShoppingCart size={16} />,
};

export default FloatingSegmentMenu;
